// src/encounter/pre-encounter-parts.tsx
// What the PRE-ENCOUNTER screen still needs from the app once its anatomy renders from Signal DS.
//
// 3A lane DS-B (2026-09-23) graduated this file's hand-built parts into the DS — the rail section
// card and its row, the chrome glyphs, the vitals tile / acuity tile / vitals header, the virtual
// visit notice and the empty card. They are imported from `@scripsteam/scrips-signal-ds` now; the
// PA copies are deleted (the shadow guard fails on any that come back).
//
// WHAT STAYS, and why it cannot move:
//   · `RailAnchoredSurface` — the anchored popover every rail `+` and dialog-backed row action
//     opens. The DS card owns the BUTTON and hands it to a host factory as `trigger`; the surface
//     around it depends on this app's `AddSurfaceInPopover` context (the dialog CONTENT publishes
//     its title / commit / filter up to the container), which the DS cannot own.
//   · `railSectionCard` — maps the rail's per-section renderers onto the DS card's surface
//     factories, so `patient-summary-rail.tsx` keeps building plain `IpsSection` view models.
//   · `SECTION_LABEL_CLASS` — the main column's tracked section label.
import { useCallback, useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  AddSurfaceInPopover,
  type AddSurfaceCloseRef,
  type RailSurfaceHeader,
} from './add-surface';
import {
  AnchoredPopover,
  AnchoredPopoverTrigger,
  SummarySectionPopover,
  type IpsSection,
  type SummarySectionCardProps,
  type SummarySectionSurface,
  type SummarySectionTone,
} from '@scripsteam/scrips-signal-ds';
import { V2_EYEBROW } from './lib/design-v2';

/** Handoff `.sectionLabel`: sans / 11px (`--text-xs`) / bold / tracked 0.09em
 *  (`--tracking-section-label`) / uppercase / brand-depth. */
export const SECTION_LABEL_CLASS =
  'font-sans text-size-xs font-bold tracking-section-label uppercase text-brand-depth';
/** Design sheet v1.1 §1 (switch ON only): the same label as the DS caps-label role — 12 / bold /
 *  uppercase, text-secondary. Labels whisper; brand stays on actions. */
export const SECTION_LABEL_CLASS_V2 = `font-sans ${V2_EYEBROW}`;

/** A rail surface renderer: the dialog content, told how to dismiss its own surface. */
export type RailSurfaceRender = (args: { close: () => void }) => ReactNode;

/**
 * The rail's anchored surface — the trigger + content machinery, once, for the `+` and every
 * dialog-backed row action (Ahmed, twice on 2026-09-07: no control in the pre-encounter left
 * column may open a centred modal).
 *
 * `trigger` is the DS card's own button, handed to `AnchoredPopoverTrigger` via `render`, which
 * REPLACES the element rather than wrapping it — so the button keeps its measured geometry and no
 * button is nested inside another button.
 *
 * Controlled `open`, but the trigger is the only thing that opens it and @base-ui's own dismissal
 * is the only thing that closes it — no host `onClick` toggle. The DS docblock records that
 * toggling `open` from an `onClick` is measurably broken: dismissal fires `onOpenChange(false)`
 * and the handler flips it back, so a second press never closes the surface.
 *
 * DETACHED mode (no `trigger`, PROD-2743 chart column behind `?ef=frameV2`): the DS
 * `ClinicalTermRow` owns its action buttons and takes no surface factory, so its buttons cannot be
 * handed to `AnchoredPopoverTrigger`. The host mounts this with the pressed button as `anchor`
 * instead: the surface opens on mount, positions against that element, and reports
 * `onOpenChange(false)` when it is dismissed so the host can unmount it. With a `trigger` nothing
 * below changes (the anchor prop is not even passed).
 */
/** The DS rail section card a `+` or row glyph lives in. */
const RAIL_CARD = '[data-slot="summary-section-card"]';

/**
 * Where a `detail` surface hangs from: the card's LEFT and RIGHT edges, the glyph's TOP and BOTTOM.
 *
 * Why not the glyph, and why not the card. The rail's anchoring (`align="end"` under the glyph) was
 * measured for the 311px rail chrome, which is the card's own width and so sits flush with it. The
 * `detail` chrome is 336, wider than the card: measured at 1280 in the PA Storybook, `align="end"`
 * put its left edge 23.5px left of the 56px PractitionerNav strip's right edge, i.e. over the nav
 * (and the 400px it replaced reached the viewport edge). Flutter opens Medication Details beside
 * the `+` over the MAIN column, never over the nav. Starting the surface at the card's left edge
 * keeps all of it to the right of the nav; its extra 25px runs into the gutter towards the main
 * column. The card alone would be the wrong anchor vertically: `side="bottom"` would then drop the
 * surface under the WHOLE card (a long medication list), not under the glyph that opened it.
 *
 * Falls back to the glyph itself when it is not inside a rail card. `contextElement` lets Base UI
 * track the glyph's scroll ancestors, as it does for a real element anchor.
 */
function cardColumnAnchor(glyph: Element | null): Element | { getBoundingClientRect: () => DOMRect; contextElement: Element } | null {
  if (!glyph) return null;
  const card = glyph.closest(RAIL_CARD);
  if (!card) return glyph;
  return {
    contextElement: glyph,
    getBoundingClientRect: () => {
      const c = card.getBoundingClientRect();
      const g = glyph.getBoundingClientRect();
      return new DOMRect(c.left, g.top, c.width, g.height);
    },
  };
}

export function RailAnchoredSurface({
  label,
  trigger,
  anchor,
  render,
  onOpenChange,
}: {
  label: string;
  /** The button the DS trigger replaces. Carries its own `aria-label` and geometry. */
  trigger?: ReactElement;
  /** DETACHED mode only: the element the surface positions against. Ignored with a `trigger`. */
  anchor?: Element | null;
  render: RailSurfaceRender;
  /** Side-effect notification only (the DS card's reveal pin, a lazy query gate, a record fetch
   *  on open). The state itself stays here; a host that also WROTE it would be the broken toggle. */
  onOpenChange?: (open: boolean) => void;
}) {
  const detached = trigger === undefined;
  const [open, setOpen] = useState(detached);
  // Where the dialog inside publishes its `onClose`, so a dismissal can clean up host state (a
  // half-typed search query, a selected drug feeding the interaction check). Run from HERE, on a
  // real `onOpenChange(false)` — not from the surface's own unmount: StrictMode double-invokes
  // effects in dev, so an unmount cleanup closed the popover the instant it opened.
  const closeRef = useRef<AddSurfaceCloseRef['current']>(null);
  const change = (next: boolean) => {
    setOpen(next);
    if (!next) {
      // Take it before calling, so a re-entrant close cannot run it twice.
      const run = closeRef.current;
      closeRef.current = null;
      run?.();
    }
    onOpenChange?.(next);
  };
  // THE HEADER TRAVELS UP. The DS `SummarySectionPopover` takes the title and the commit action as
  // PROPS, but both are declared by the CONTENT, which renders as its children. The content
  // publishes them; this holds them. Published BY VALUE — a callback published as state would
  // re-render this surface, re-run `render`, hand the dialog a new `onCommit` and publish again
  // (an unbounded loop) — so the callback is reached through `commitRef` and only its PRESENCE is
  // state.
  const [header, setHeader] = useState<RailSurfaceHeader>({ hasCommit: false, reserve: false });
  const commitRef = useRef<(() => void) | undefined>(undefined);
  const publishHeader = useCallback((next: RailSurfaceHeader) => {
    setHeader((prev) =>
      prev.title === next.title &&
      prev.hasCommit === next.hasCommit &&
      prev.reserve === next.reserve &&
      prev.filterable === next.filterable &&
      prev.confirmLabel === next.confirmLabel &&
      prev.width === next.width &&
      prev.variant === next.variant &&
      prev.showConfirm === next.showConfirm
        ? prev
        : next,
    );
  }, []);
  // The DS `filter` band's slot. Only the boolean `filterable` travels up; the body PORTALS its
  // field into this element, so every keystroke stays in the body.
  const [filterSlot, setFilterSlot] = useState<HTMLElement | null>(null);
  const commit = useCallback(() => commitRef.current?.(), []);
  // The element the surface opened from: the `+`/row glyph (trigger mode) or the host's anchor.
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const detailAnchor = useCallback(
    () => cardColumnAnchor(detached ? (anchor ?? null) : triggerRef.current),
    [detached, anchor],
  );
  return (
    <AnchoredPopover open={open} onOpenChange={change}>
      {trigger === undefined ? null : <AnchoredPopoverTrigger ref={triggerRef} render={trigger} />}
      {/* The container is the Signal DS molecule, which owns every pixel of it. Passed here is
          only the rail's ANCHORING, measured live at 1280x688:
          `side="bottom" align="end"` puts the surface under the `+`, flush with the card's right
          edge. `align: 'shift'` — `align="end"` grows the surface LEFTWARD from an anchor ~357px
          from the viewport edge, so a 480px form would land at left -123. `side: 'flip'` — the
          rail is a tall scrolling column, so a `+` low on screen has almost no room beneath it
          (Social History clamped to a 36px body when pinned to `bottom`).
          `bodyClassName` is the DS's sanctioned escape for a body whose rows carry their own
          inset (a picker row is full-bleed), so the molecule's default padding does not double it.
          `aria-label` ONLY when there is no title: @base-ui wires the title as the popup's
          `aria-labelledby`, which outranks `aria-label`. */}
      {/* Every surface hangs from the card's left edge (16 Oct: the add surfaces are 400 wide now, and `align="end"` would put
          their left edge over the nav; the `detail` Flutter popup always did): see `cardColumnAnchor`. */}
      <SummarySectionPopover
        side="bottom"
        align="start"
        sideOffset={6}
        collisionAvoidance={{ side: 'flip', align: 'shift' }}
        title={header.title}
        onConfirm={header.hasCommit ? commit : undefined}
        confirmLabel={header.confirmLabel}
        showConfirm={header.showConfirm}
        variant={header.variant}
        width={header.width}
        aria-label={header.title ? undefined : label}
        bodyHeight={header.reserve ? 'reserved' : 'auto'}
        filter={header.filterable ? <div ref={setFilterSlot} className="contents" /> : undefined}
        bodyClassName="gap-0 px-0 pt-0"
        {...{ anchor: detailAnchor }}
      >
        {/* Marks the subtree as anchored, so a dialog rendered here emits the popover content
            instead of its centred `Modal` — see `add-surface.tsx`. The SAME dialog rendered from
            the in-encounter banner is outside this provider and keeps the modal. */}
        <AddSurfaceInPopover
          closeRef={closeRef}
          commitRef={commitRef}
          publishHeader={publishHeader}
          hostsFilter
          filterSlot={filterSlot}
        >
          {render({ close: () => change(false) })}
        </AddSurfaceInPopover>
      </SummarySectionPopover>
    </AnchoredPopover>
  );
}

/** A DS surface factory that opens `render` in the rail's anchored surface. `hostOpenChange` is
 *  forwarded ALONGSIDE the DS card's own notification (which pins a row's hover-revealed glyph
 *  visible while its surface is up). */
function anchoredSurface(
  render: RailSurfaceRender,
  hostOpenChange?: (open: boolean) => void,
): SummarySectionSurface {
  return ({ label, trigger, onOpenChange }) => (
    <RailAnchoredSurface
      label={label}
      trigger={trigger}
      render={render}
      onOpenChange={(open) => {
        onOpenChange(open);
        hostOpenChange?.(open);
      }}
    />
  );
}

export interface RailSectionCardOptions {
  section: IpsSection;
  /** Stripe + title tone. Defaults to success for Resolved Problems (the DS `IpsAccent` union has
   *  no success member), otherwise the DS card reads `section.accent`. */
  tone?: SummarySectionTone;
  emptyLabel?: string;
  /** Accessible name for the `+`. Single-record sections pass "Edit …" once populated. */
  addLabel?: string;
  /** Render the add surface ANCHORED to the card's `+` instead of firing `section.onAdd`. Absent →
   *  the `+` calls `section.onAdd`, so a section still on its centred `Modal` is unaffected. */
  addPopover?: RailSurfaceRender;
  /** Side-effect notification that the add surface opened or closed (a lazy query gate). */
  onAddOpenChange?: (open: boolean) => void;
  /** Render a ROW action's surface anchored to that action's glyph, looked up by (entry id,
   *  action label). A lookup rather than a field because `IpsEntry.actions` is a DS type the
   *  in-encounter banner shares. `undefined` leaves the action a plain button firing its own
   *  `onClick` — that is how Remove / Resolve / Unresolve stay direct mutations. */
  actionPopover?: (entryId: string, actionLabel: string) => RailSurfaceRender | undefined;
  /** The row-action twin of `onAddOpenChange`. */
  actionOpenChange?: (entryId: string, actionLabel: string) => ((open: boolean) => void) | undefined;
  /** Design sheet v1.1 switch (`useDesignV2`). ON: status colour is never a CATEGORY (§1), so every
   *  section takes the DS `neutral` tone except Allergies, which keeps its error accent (the one
   *  safety exception). The DS owns what `neutral` looks like; the 2.14.0 wayfinding tokens replace
   *  it in `V2_RAIL_TONE` alone. OFF/absent: today's tones, unchanged. */
  designV2?: boolean;
}

/** Switch-ON section tones (design sheet v1.1 §2.7). Allergies is absent on purpose: it keeps the
 *  section's own `error` accent. */
const V2_RAIL_TONE: Readonly<Record<string, SummarySectionTone>> = {
  flags: 'neutral',
  medications: 'neutral',
  'resolved-problems': 'neutral',
  'active-problems': 'neutral',
};

/** The rail's section view model → DS `SummarySectionCard` props, with its anchored surfaces. */
export function railSectionCard({
  section,
  tone,
  emptyLabel,
  addLabel,
  addPopover,
  onAddOpenChange,
  actionPopover,
  actionOpenChange,
  designV2,
}: RailSectionCardOptions): SummarySectionCardProps {
  return {
    section,
    tone: designV2
      ? (tone ?? V2_RAIL_TONE[section.id])
      : tone ?? (section.id === 'resolved-problems' ? 'success' : undefined),
    emptyLabel,
    addLabel,
    addSurface: addPopover ? anchoredSurface(addPopover, onAddOpenChange) : undefined,
    actionSurface: actionPopover
      ? (entryId, actionLabel) => {
          const render = actionPopover(entryId, actionLabel);
          return render ? anchoredSurface(render, actionOpenChange?.(entryId, actionLabel)) : undefined;
        }
      : undefined,
  };
}
